import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { buildTrackingUrl, sanitizeUtmValue } from '~/utils/campaignLinksUtils';

import { COMMERCIAL_TYPES, PLATFORMS } from './constants';
import type { CommercialType, Platform } from './constants';

interface FormValues {
  baseUrl: string;
  platform: Platform | '';
  commercialType: CommercialType | '';
  campaign: string;
  handle: string;
}

interface FormErrors {
  baseUrl?: string;
  platform?: string;
  commercialType?: string;
  campaign?: string;
  handle?: string;
}

const INITIAL: FormValues = {
  baseUrl: '',
  platform: '',
  commercialType: '',
  campaign: '',
  handle: '',
};

function validate(values: FormValues): FormErrors {
  const errors: FormErrors = {};

  if (!values.baseUrl.trim()) {
    errors.baseUrl = 'Base URL is required.';
  } else if (!/^https?:\/\/.+/i.test(values.baseUrl.trim())) {
    errors.baseUrl = 'Must start with http:// or https://';
  }

  if (!values.platform) {
    errors.platform = 'Select a platform.';
  }

  if (!values.commercialType) {
    errors.commercialType = 'Select a commercial type.';
  }

  if (!values.campaign.trim()) {
    errors.campaign = 'Campaign name is required.';
  } else if (!sanitizeUtmValue(values.campaign)) {
    errors.campaign = 'Campaign name has no valid characters after sanitization.';
  }

  if (!values.handle.trim()) {
    errors.handle = 'Talent handle is required.';
  } else if (!sanitizeUtmValue(values.handle)) {
    errors.handle = 'Handle has no valid characters after sanitization.';
  }

  return errors;
}

export default function CampaignLinksPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [values, setValues] = useState<FormValues>(INITIAL);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const errors = useMemo(() => validate(values), [values]);
  const isValid = Object.keys(errors).length === 0;

  const sanitizedCampaign = useMemo(() => sanitizeUtmValue(values.campaign), [values.campaign]);
  const sanitizedHandle = useMemo(() => sanitizeUtmValue(values.handle), [values.handle]);

  const generatedUrl = useMemo(() => {
    if (!isValid) return '';
    return buildTrackingUrl(
      values.baseUrl.trim(),
      values.platform,
      values.commercialType,
      sanitizedCampaign,
      sanitizedHandle,
    );
  }, [isValid, values.baseUrl, values.platform, values.commercialType, sanitizedCampaign, sanitizedHandle]);

  const utmPreview = useMemo(() => {
    if (!isValid) return null;
    return {
      utm_source: values.platform,
      utm_medium: values.commercialType,
      utm_campaign: sanitizedCampaign,
      utm_content: sanitizedHandle,
    };
  }, [isValid, values.platform, values.commercialType, sanitizedCampaign, sanitizedHandle]);

  const set = useCallback(
    (field: keyof FormValues, value: string) => setValues((prev) => ({ ...prev, [field]: value })),
    [],
  );

  const touch = useCallback((field: string) => setTouched((prev) => ({ ...prev, [field]: true })), []);

  const handleCopy = useCallback(async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Unable to copy — please copy the URL manually', {
        variant: 'warning',
      });
    }
  }, [generatedUrl, enqueueSnackbar]);

  const handleReset = useCallback(() => {
    setValues(INITIAL);
    setTouched({});
  }, []);

  const showError = (field: string) => touched[field] && errors[field as keyof FormErrors];

  return (
    <Container maxWidth="xl">
      <Box sx={{ maxWidth: 720, mx: 'auto', mt: 2.5 }}>
        <Stack direction="column" gap={0.75} sx={{ pb: 1.5 }}>
          <Typography variant="h4">Campaign Links</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Generate LE-compliant tracking links for campaigns and talent partnerships
          </Typography>
        </Stack>
        <Divider />
        <Box sx={{ mt: 2 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              label="Base / Landing URL"
              type="url"
              placeholder="https://luxuryescapes.com/sg/offer/..."
              value={values.baseUrl}
              onChange={(e) => set('baseUrl', e.target.value)}
              onBlur={() => touch('baseUrl')}
              error={!!showError('baseUrl')}
              helperText={showError('baseUrl') ? errors.baseUrl : undefined}
            />

            <FormControl fullWidth required error={!!showError('platform')}>
              <InputLabel id="campaign-links-platform-label">Social Platform</InputLabel>
              <Select
                labelId="campaign-links-platform-label"
                value={values.platform}
                label="Social Platform"
                onChange={(e) => set('platform', e.target.value)}
                onBlur={() => touch('platform')}
              >
                <MenuItem value="" disabled>
                  Choose platform…
                </MenuItem>
                {PLATFORMS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
              {showError('platform') && <FormHelperText>{errors.platform}</FormHelperText>}
            </FormControl>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Influencer commercial type
              </Typography>
              <ToggleButtonGroup
                value={values.commercialType}
                exclusive
                onChange={(_, value) => {
                  if (value != null) {
                    set('commercialType', value);
                    touch('commercialType');
                  }
                }}
                fullWidth
                size="small"
              >
                {COMMERCIAL_TYPES.map((ct) => (
                  <ToggleButton key={ct.value} value={ct.value}>
                    {ct.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {showError('commercialType') && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {errors.commercialType}
                </FormHelperText>
              )}
            </Box>

            <TextField
              fullWidth
              required
              label="Campaign name"
              placeholder="sg_mar25_bali_launch"
              value={values.campaign}
              onChange={(e) => set('campaign', e.target.value)}
              onBlur={() => touch('campaign')}
              error={!!showError('campaign')}
              helperText={
                showError('campaign')
                  ? errors.campaign
                  : values.campaign && !errors.campaign
                  ? `→ ${sanitizedCampaign}`
                  : 'Use a short, consistent campaign name (e.g. sg_mar25_bali_launch)'
              }
            />

            <TextField
              fullWidth
              required
              label="Talent handle (without @)"
              placeholder="gribeshhours"
              value={values.handle}
              onChange={(e) => set('handle', e.target.value)}
              onBlur={() => touch('handle')}
              error={!!showError('handle')}
              helperText={
                showError('handle')
                  ? errors.handle
                  : values.handle && !errors.handle
                  ? `→ ${sanitizedHandle}`
                  : undefined
              }
            />
          </Stack>

          {utmPreview && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
              >
                UTM Preview
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
                {(Object.entries(utmPreview) as [string, string][]).map(([key, val]) => (
                  <Box key={key} sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    <Typography component="span" variant="body2" color="text.secondary">
                      {key}
                    </Typography>
                    <Typography component="span" variant="body2" fontWeight={500} sx={{ ml: 1 }}>
                      {val}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              Generated URL
            </Typography>
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: generatedUrl ? 'action.hover' : 'action.selected',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap',
                minHeight: 56,
              }}
            >
              {generatedUrl || 'Fill in all fields to generate a URL…'}
            </Box>
          </Box>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" color="primary" onClick={handleCopy} disabled={!isValid || !generatedUrl}>
              Copy link
            </Button>
            <Button variant="text" onClick={handleReset}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Box>
    </Container>
  );
}
